import { SearchOutlined } from '@ant-design/icons';
import { type InputRef, Button } from 'antd';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AutoFocus } from 'horizon/components/AutoFocus';

import {
  CustomFilterPopover,
  FilterActions,
  FilterSearchButton,
  FilterSearchInput,
} from './filterPopover.style';

type IFilterDropDownProps = {
  defaultValue: string;
  confirm: (param?: FilterConfirmProps) => void;
  clearFilters(): void;
  selectedKeys: string[];
  setSelectedKeys(selectedKeys: string[]): any;
  visible?: boolean;
};

export const FilterDropdown: React.FunctionComponent<IFilterDropDownProps> = ({
  defaultValue,
  confirm,
  clearFilters,
  selectedKeys,
  setSelectedKeys,
  visible,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<InputRef>(null);
  const [value, setValue] = useState<string>('');

  useEffect(() => {
    // When dropdown becomes visible set our value to the selectedKeys.
    if (visible) {
      // If the selectedKeys is empty: either first time it's been opened OR we've reset our filters and need to reset our value
      setValue(selectedKeys[0] ?? '');
    }
  }, [visible, selectedKeys]);

  useEffect(() => {
    // defaultValue is null if HoC queries haven't loaded data yet
    // this ensures that defaultValue is used, even if the component already
    // renders with a null value
    if (defaultValue && !value) {
      setValue(defaultValue);
    }
  }, [defaultValue, value]);

  // e is typed as any here because Antd requires a specific shape for the input but doesn't define a public type for what that shape is.
  const handleChange = (e: any) => {
    const newValue: string = [e.target.value].find(Boolean) ?? '';
    setValue(newValue);
    setSelectedKeys([newValue]);
  };

  // clearFilters only resets the draft; confirm applies it.
  const handleReset = () => {
    setValue('');
    clearFilters();
    confirm({ closeDropdown: true });
  };

  const handleExit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key.match('Escape')) {
      handleReset();
    }
  };

  const handleSearch = () => {
    if (value && value.length > 0) {
      confirm({ closeDropdown: true });
    } else {
      handleReset();
    }
  };

  return (
    <AutoFocus elementToFocus={inputRef}>
      <CustomFilterPopover
        className="ant-table-filter-dropdown"
        role="dialog"
        aria-label={t('common.filter', 'Filter')}
        onKeyUp={handleExit}
      >
        <div style={{ padding: '.5rem' }}>
          <FilterSearchInput
            ref={inputRef}
            aria-label={t('common.search', 'Search')}
            onChange={handleChange}
            onPressEnter={handleSearch}
            placeholder={t('common.search', 'Search')}
            suffix={
              <FilterSearchButton
                type="button"
                aria-label={t('common.search', 'Search')}
                onClick={handleSearch}
              >
                <SearchOutlined aria-hidden />
              </FilterSearchButton>
            }
            value={value}
          />
        </div>
        <FilterActions>
          <Button type="link" size="small" onClick={handleReset}>
            {t('table.reset', 'Reset')}
          </Button>
          <Button type="primary" size="small" onClick={handleSearch}>
            {t('table.ok', 'OK')}
          </Button>
        </FilterActions>
      </CustomFilterPopover>
    </AutoFocus>
  );
};
